/**
 * Auth API client.
 *
 * WHY THERE IS NO TOKEN HANDLING IN THIS FILE
 *
 * The session id lives in a `__Host-` prefixed HttpOnly cookie. JavaScript in
 * this app cannot read it, cannot write it, and never sees it. That is
 * deliberate and it is the main reason the whole file looks this plain: every
 * request just sets `credentials: "include"` and the browser attaches the
 * cookie itself.
 *
 * Do not add a localStorage or sessionStorage path for any auth material. The
 * editor renders user supplied filenames, captions and model output, so an XSS
 * in this app is a realistic scenario. A token in web storage is readable by
 * any script on the page and would be exfiltrated in a single line. An
 * HttpOnly cookie is not, which means an XSS can still act as the user while
 * the page is open but cannot walk away with a portable credential.
 *
 * The only auth value JS does touch is the CSRF token, which is intentionally
 * readable, is useless without the session cookie, and is echoed back in a
 * header so the server can compare the two.
 */

import { OPENREEL_AUTH_URL } from "../../config/api-endpoints";
import {
  AuthApiError,
  AuthNetworkError,
  type AuthConnection,
  type AuthSession,
  type AuthSessionRecord,
  type AuthUser,
  type EmailOtpVerifyResult,
  type OnboardingState,
} from "./types";

/**
 * A hanging socket must not strand the UI in "loading" forever, so every
 * request carries its own abort timer.
 */
export const AUTH_REQUEST_TIMEOUT_MS = 10_000;

const CSRF_HEADER = "X-CSRF-Token";

/**
 * The `__Host-` prefixed name is what the server sets, and it must match
 * CSRF_COOKIE in the auth Worker's `src/csrf.ts` exactly. A mismatch here is
 * silent and total: the token is never found, the header is never sent, and
 * every write comes back 403 with nothing in the browser to explain why.
 *
 * The unprefixed names are a fallback for local setups where the prefix rules
 * (Secure, Path=/, no Domain) cannot be met.
 */
const CSRF_COOKIE_NAMES = ["__Host-flshy_csrf", "flshy_csrf", "__Host-csrf", "csrf"];

/**
 * The authorize URL comes from our own server, but it ends up in a top level
 * navigation, so it gets checked anyway. This blocks a tampered or buggy
 * response from turning into a `javascript:` navigation or an open redirect.
 */
const AUTHORIZE_URL_HOSTS = ["accounts.google.com"];

const PATHS = {
  me: "/auth/me",
  oauthStart: "/auth/oauth/start",
  magicRequest: "/auth/magic/request",
  emailStart: "/auth/email/start",
  emailVerify: "/auth/email/verify",
  onboarding: "/auth/onboarding",
  logout: "/auth/logout",
  profile: "/auth/profile",
  sessions: "/auth/sessions",
  revokeSession: "/auth/sessions/revoke",
  revokeAllSessions: "/auth/sessions/revoke-all",
  deleteAccount: "/auth/account/delete",
} as const;

type HttpMethod = "GET" | "POST" | "PATCH";

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  timeoutMs?: number;
}

function baseUrl(): string {
  return OPENREEL_AUTH_URL.replace(/\/+$/, "");
}

function isStateChanging(method: HttpMethod): boolean {
  return method !== "GET";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Read the double submit CSRF value from the readable cookie.
 *
 * Exported for tests and for the UI lane if it ever needs to know whether a
 * write is going to be rejected before it fires one.
 */
export function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const jar = document.cookie || "";
  if (jar.length === 0) return null;

  const entries = new Map<string, string>();
  for (const part of jar.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const name = part.slice(0, eq).trim();
    const value = part.slice(eq + 1).trim();
    if (name.length > 0 && value.length > 0 && !entries.has(name)) {
      entries.set(name, value);
    }
  }

  for (const name of CSRF_COOKIE_NAMES) {
    const raw = entries.get(name);
    if (raw) {
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return null;
}

/**
 * The last CSRF token the server handed back in a response body.
 *
 * In production the API is a different origin from the editor (api. vs app.)
 * and the CSRF cookie carries the __Host- prefix, which forbids a Domain
 * attribute and pins the cookie to the API host. The browser still sends it
 * back on credentialed requests, but document.cookie here can never see it, so
 * readCsrfToken() returns null forever and every write fails the double submit
 * with a 403. The server therefore also returns the value in the body of the
 * calls that issue it, and that is what this holds.
 */
let bodyCsrfToken: string | null = null;

/** Pick up the token from any response that carries one. */
export function rememberCsrfToken(body: unknown): void {
  if (body && typeof body === "object") {
    const token = (body as { csrfToken?: unknown }).csrfToken;
    if (typeof token === "string" && token.length > 0) bodyCsrfToken = token;
  }
}

/**
 * Drop the cached token.
 *
 * Called on logout: the server clears the CSRF cookie there, so holding the
 * old value would leave this module offering a token the server has already
 * forgotten, and a subsequent sign-in as somebody else would start by sending
 * the previous session's. Tests use it to isolate the module state.
 */
export function clearCsrfToken(): void {
  bodyCsrfToken = null;
}

/**
 * Get a CSRF token, priming it with a GET if we do not have one yet.
 *
 * The body token wins over the cookie: cross origin it is the only one that
 * exists, and same origin the two are the same value anyway. The cookie read
 * stays as the fallback so a same origin deployment keeps working even if the
 * server stops echoing the token.
 *
 * A first time visitor has neither until the server issues one, and the first
 * thing they do may well be a write (POST /auth/oauth/start). GET /auth/me is
 * safe to fire for that purpose: it is idempotent, it is the same call
 * hydration makes, and it both sets the cookie and returns the token.
 *
 * If there is still nothing, the request goes out without the header. Client
 * side CSRF checks protect nobody: the server does the timing safe comparison
 * and rejects. Failing open here avoids deadlocking sign in against a server
 * that issues the token at some other point.
 */
async function ensureCsrfToken(): Promise<string | null> {
  const existing = bodyCsrfToken ?? readCsrfToken();
  if (existing) return existing;
  try {
    await rawFetch(PATHS.me, { method: "GET" });
  } catch {
    // Swallowed on purpose: the real write below reports the real failure.
  }
  return bodyCsrfToken ?? readCsrfToken();
}

async function rawFetch(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? "GET";
  const timeoutMs = options.timeoutMs ?? AUTH_REQUEST_TIMEOUT_MS;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (isStateChanging(method)) {
    const csrf = await ensureCsrfToken();
    if (csrf) {
      headers[CSRF_HEADER] = csrf;
    } else {
      console.warn("[auth] no csrf cookie yet, the server may reject this write");
    }
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(`${baseUrl()}${path}`, {
      method,
      headers,
      // The session cookie rides on this and nothing else. Without it the
      // request is anonymous no matter what else we send.
      credentials: "include",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
    });
  } catch (cause) {
    if (controller.signal.aborted) {
      throw new AuthNetworkError(
        `auth request timed out after ${timeoutMs}ms`,
        true,
        cause,
      );
    }
    throw new AuthNetworkError("auth request could not reach the server", false, cause);
  } finally {
    clearTimeout(timer);
  }
}

async function readJson(res: Response): Promise<Record<string, unknown>> {
  if (res.status === 204) return {};
  try {
    const parsed = await res.json();
    const body = isRecord(parsed) ? parsed : {};
    // Every parsed response passes through here, so this is the one place that
    // needs to notice a token. Endpoints that do not carry one leave it alone.
    rememberCsrfToken(body);
    return body;
  } catch {
    return {};
  }
}

/**
 * Build an AuthApiError from a failed response.
 *
 * Only a machine readable `code` is pulled out of the body. Server prose is
 * not forwarded into the message, so nothing that might carry an email or a
 * token ends up in a log line or a toast.
 */
async function apiError(res: Response, label: string): Promise<AuthApiError> {
  const body = await readJson(res);
  const code = asString(body.code) ?? asString(body.error);
  return new AuthApiError(`${label} failed with ${res.status}`, res.status, code);
}

const KNOWN_CONNECTIONS: readonly AuthConnection[] = ["email", "google"];

/**
 * Only connections this build knows how to render survive. A value we do not
 * recognise is dropped rather than shown, because the connected accounts UI
 * would otherwise print server text straight into the page.
 */
function normalizeConnections(raw: unknown): AuthConnection[] {
  if (!Array.isArray(raw)) return [];
  return KNOWN_CONNECTIONS.filter((known) => raw.includes(known));
}

function normalizeUser(raw: unknown): AuthUser | null {
  if (!isRecord(raw)) return null;
  const id = asString(raw.id);
  const email = asString(raw.email);
  if (!id || !email) return null;
  return {
    id,
    email,
    name: asString(raw.name),
    firstName: asString(raw.firstName),
    lastName: asString(raw.lastName),
    avatarUrl: asString(raw.avatarUrl),
    createdAt: asString(raw.createdAt) ?? "",
    connections: normalizeConnections(raw.connections),
  };
}

/**
 * Coerce one session row, or drop it.
 *
 * A row missing its id cannot be revoked, so rendering it would put a dead
 * button in a security surface. Missing labels become the same "Unknown" the
 * server uses rather than an empty cell.
 */
function normalizeSessionRecord(raw: unknown): AuthSessionRecord | null {
  if (!isRecord(raw)) return null;
  const id = asString(raw.id);
  if (!id) return null;
  return {
    id,
    device: asString(raw.device) ?? "Unknown device",
    location: asString(raw.location) ?? "Unknown",
    createdAt: asString(raw.createdAt) ?? "",
    lastActiveAt: asString(raw.lastActiveAt) ?? "",
    current: raw.current === true,
  };
}

/**
 * Coerce whatever came back into the exact OnboardingState shape.
 *
 * The onboarding UI reads these fields directly, so a missing key must land as
 * null rather than undefined and a missing object must not crash the app.
 */
export function normalizeOnboarding(raw: unknown): OnboardingState {
  const source = isRecord(raw) ? raw : {};
  return {
    completed: source.completed === true,
    role: asString(source.role),
    source: asString(source.source),
    sourceDetail: asString(source.sourceDetail),
    intent: asString(source.intent),
  };
}

function assertSafeAuthorizeUrl(value: string): void {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new AuthApiError(
      "oauth start returned an unusable authorizeUrl",
      200,
      "unsafe_authorize_url",
    );
  }
  if (parsed.protocol !== "https:" || !AUTHORIZE_URL_HOSTS.includes(parsed.hostname)) {
    throw new AuthApiError(
      `oauth start returned a non allowlisted authorize host: ${parsed.protocol}//${parsed.hostname}`,
      200,
      "unsafe_authorize_url",
    );
  }
}

/**
 * GET /auth/me
 *
 * Returns null when the server says 401, which is the one answer that proves
 * there is no session. Anything else throws so the caller can tell "signed
 * out" apart from "could not ask".
 */
export async function fetchMe(): Promise<AuthSession | null> {
  const res = await rawFetch(PATHS.me, { method: "GET" });
  if (res.status === 401) return null;
  if (!res.ok) throw await apiError(res, "GET /auth/me");

  const body = await readJson(res);
  const user = normalizeUser(body.user);
  if (!user) {
    throw new AuthApiError("GET /auth/me returned no usable user", res.status, "malformed_me");
  }
  return { user, onboarding: normalizeOnboarding(body.onboarding) };
}

/**
 * POST /auth/oauth/start, then navigate to the returned authorize URL.
 *
 * PKCE verifier and the single use state parameter are generated and stored
 * server side. Nothing about them touches this file, which is why there is no
 * crypto here.
 */
export async function startGoogleOAuth(): Promise<void> {
  const res = await rawFetch(PATHS.oauthStart, { method: "POST", body: {} });
  if (!res.ok) throw await apiError(res, "POST /auth/oauth/start");

  const body = await readJson(res);
  const authorizeUrl = asString(body.authorizeUrl);
  if (!authorizeUrl) {
    throw new AuthApiError(
      "oauth start returned no authorizeUrl",
      res.status,
      "unsafe_authorize_url",
    );
  }
  assertSafeAuthorizeUrl(authorizeUrl);
  window.location.assign(authorizeUrl);
}

/**
 * POST /auth/magic/request
 *
 * The server answers the same way whether or not the address has an account,
 * so there is deliberately nothing in the return value to inspect: reporting
 * anything more here would rebuild the account enumeration oracle the server
 * is avoiding. The email is never logged.
 */
export async function requestMagicLink(email: string): Promise<void> {
  const trimmed = email.trim();
  if (trimmed.length === 0) {
    throw new AuthApiError("an email address is required", 400, "invalid_email");
  }
  const res = await rawFetch(PATHS.magicRequest, { method: "POST", body: { email: trimmed } });
  if (!res.ok) throw await apiError(res, "POST /auth/magic/request");
}

/**
 * POST /auth/email/start
 *
 * Asks the server to mint a 6-digit code and email it. Same as the magic link
 * request, the server answers 200 whether or not the address has an account, so
 * there is nothing in the return value to inspect: reading a difference here
 * would rebuild the account-enumeration oracle the server is avoiding. The code
 * itself is never returned to the browser (it is hashed server-side and only
 * emailed), so there is nothing code-shaped to hold on to here either. The email
 * is never logged.
 */
export async function requestEmailOtp(email: string): Promise<void> {
  const trimmed = email.trim();
  if (trimmed.length === 0) {
    throw new AuthApiError("an email address is required", 400, "invalid_email");
  }
  const res = await rawFetch(PATHS.emailStart, { method: "POST", body: { email: trimmed } });
  if (!res.ok) throw await apiError(res, "POST /auth/email/start");
  // Read the body even though nothing here needs it: this is the call that
  // bootstraps the CSRF token, and the verify that follows cannot pass the
  // double submit without it. readJson is what captures it.
  await readJson(res);
}

/**
 * POST /auth/email/verify
 *
 * Trades a correct code for a session: the server sets the __Host- session and
 * CSRF cookies on this response, exactly as OAuth and magic link do, and this
 * function never sees or stores them. What it returns is the same session shape
 * GET /auth/me gives, plus `isNewUser` so the caller can route a first-timer
 * into onboarding.
 *
 * A wrong or expired code comes back 400 with `invalid_code`, and too many
 * attempts come back 429. Both surface as an AuthApiError the caller can read
 * `status`/`code` off to phrase the right message. The plaintext code only ever
 * travels up in the request body; nothing about it is written to storage.
 */
export async function verifyEmailOtp(
  email: string,
  code: string,
): Promise<EmailOtpVerifyResult> {
  const trimmedEmail = email.trim();
  // Strip spacing/formatting so a pasted "123 456" still verifies; the server
  // is the authority on what a valid code is.
  const trimmedCode = code.replace(/\s+/g, "");
  if (trimmedEmail.length === 0) {
    throw new AuthApiError("an email address is required", 400, "invalid_email");
  }
  if (trimmedCode.length === 0) {
    throw new AuthApiError("a code is required", 400, "invalid_code");
  }

  const res = await rawFetch(PATHS.emailVerify, {
    method: "POST",
    body: { email: trimmedEmail, code: trimmedCode },
  });
  if (!res.ok) throw await apiError(res, "POST /auth/email/verify");

  const body = await readJson(res);
  const user = normalizeUser(body.user);
  if (!user) {
    throw new AuthApiError(
      "POST /auth/email/verify returned no usable user",
      res.status,
      "malformed_user",
    );
  }
  return {
    user,
    onboarding: normalizeOnboarding(body.onboarding),
    isNewUser: body.isNewUser === true,
  };
}

/**
 * PATCH /auth/onboarding
 *
 * Only the four answer fields are sent. `completed` is derived server side and
 * is never something the client gets to assert, so it is dropped even when a
 * caller passes it in.
 */
export async function patchOnboarding(
  partial: Partial<OnboardingState>,
): Promise<OnboardingState> {
  const body: Record<string, string | null> = {};
  if ("role" in partial) body.role = partial.role ?? null;
  if ("source" in partial) body.source = partial.source ?? null;
  if ("sourceDetail" in partial) body.sourceDetail = partial.sourceDetail ?? null;
  if ("intent" in partial) body.intent = partial.intent ?? null;

  const res = await rawFetch(PATHS.onboarding, { method: "PATCH", body });
  if (!res.ok) throw await apiError(res, "PATCH /auth/onboarding");

  const payload = await readJson(res);
  return normalizeOnboarding(payload.onboarding ?? payload);
}

/**
 * POST /auth/logout
 *
 * A 401 counts as success: the session we were asking to destroy is already
 * gone. The server deletes its own record and clears the cookie, there is no
 * client side cleanup to do because there is nothing client side to clean.
 */
export async function logout(): Promise<void> {
  const res = await rawFetch(PATHS.logout, { method: "POST" });
  // The server clears the CSRF cookie on the way out, so the cached copy has to
  // go too, whether or not the call succeeded. Keeping it would leave the next
  // sign-in opening with the previous session's token.
  clearCsrfToken();
  if (res.status === 401) return;
  if (!res.ok) throw await apiError(res, "POST /auth/logout");
}

/**
 * PATCH /auth/profile
 *
 * Only the two name halves and an avatar removal. There is deliberately no
 * email parameter: on a passwordless account the address IS the credential, so
 * changing it is a verify-the-new-address flow, not a field edit. The server
 * has no route for it either, and would ignore the key if this sent one.
 */
export async function updateProfile(partial: {
  firstName?: string | null;
  lastName?: string | null;
}): Promise<AuthUser> {
  const body: Record<string, string | null> = {};
  if ("firstName" in partial) body.firstName = partial.firstName ?? null;
  if ("lastName" in partial) body.lastName = partial.lastName ?? null;

  const res = await rawFetch(PATHS.profile, { method: "PATCH", body });
  if (!res.ok) throw await apiError(res, "PATCH /auth/profile");

  const user = normalizeUser((await readJson(res)).user);
  if (!user) {
    throw new AuthApiError(
      "PATCH /auth/profile returned no usable user",
      res.status,
      "malformed_user",
    );
  }
  return user;
}

/**
 * PATCH /auth/profile with a null avatar.
 *
 * Removal is the only avatar operation there is. The server refuses to accept a
 * URL from a client, so there is nothing to send but null: an account photo
 * pointed at an address the client chose would be a request our users' browsers
 * make to a host somebody else picked.
 */
export async function removeAvatar(): Promise<AuthUser> {
  const res = await rawFetch(PATHS.profile, { method: "PATCH", body: { avatarUrl: null } });
  if (!res.ok) throw await apiError(res, "PATCH /auth/profile");

  const user = normalizeUser((await readJson(res)).user);
  if (!user) {
    throw new AuthApiError(
      "PATCH /auth/profile returned no usable user",
      res.status,
      "malformed_user",
    );
  }
  return user;
}

/**
 * GET /auth/sessions
 *
 * The server returns only the caller's own rows, so there is no filtering to do
 * here and none should be added: a client-side filter would imply the response
 * could ever contain somebody else's session.
 */
export async function fetchSessions(): Promise<AuthSessionRecord[]> {
  const res = await rawFetch(PATHS.sessions, { method: "GET" });
  if (!res.ok) throw await apiError(res, "GET /auth/sessions");

  const raw = (await readJson(res)).sessions;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizeSessionRecord)
    .filter((row): row is AuthSessionRecord => row !== null);
}

/**
 * POST /auth/sessions/revoke
 *
 * A 404 means the session was already gone, which is the outcome the caller
 * wanted, so it is not an error. Revoking the current session is allowed and
 * ends this browser's session too, which the caller has to be ready for.
 */
export async function revokeSession(sessionId: string): Promise<void> {
  const trimmed = sessionId.trim();
  if (trimmed.length === 0) {
    throw new AuthApiError("a sessionId is required", 400, "invalid_session_id");
  }
  const res = await rawFetch(PATHS.revokeSession, {
    method: "POST",
    body: { sessionId: trimmed },
  });
  if (res.status === 404) return;
  if (!res.ok) throw await apiError(res, "POST /auth/sessions/revoke");
}

/**
 * POST /auth/sessions/revoke-all
 *
 * Ends every session including this one, by design: the reason to press it is
 * that one of them might not be yours.
 */
export async function revokeAllSessions(): Promise<void> {
  const res = await rawFetch(PATHS.revokeAllSessions, { method: "POST" });
  if (res.status === 401) return;
  if (!res.ok) throw await apiError(res, "POST /auth/sessions/revoke-all");
}

/**
 * POST /auth/account/delete
 *
 * The typed confirmation goes to the server and is checked there. The dialog
 * checks it too, but only so the button can be disabled: a confirmation that
 * exists only in the client is a confirmation an attacker skips.
 *
 * This destroys the account on the server. It does not touch the projects in
 * this browser's IndexedDB, which are the user's own local data and are not
 * ours to delete from here.
 */
export async function deleteAccount(confirmEmail: string): Promise<void> {
  const trimmed = confirmEmail.trim();
  if (trimmed.length === 0) {
    throw new AuthApiError("a confirmation is required", 400, "invalid_confirmation");
  }
  const res = await rawFetch(PATHS.deleteAccount, {
    method: "POST",
    body: { confirmEmail: trimmed },
  });
  if (!res.ok) throw await apiError(res, "POST /auth/account/delete");
}
